/**
 * ModifyTagsPage
 *
 * Ported from:
 *   - mobile: edit_user_tags_screen.dart  (browse + toggle available tags, save selection)
 *   - mobile: manage_user_tags_screen.dart (edit / delete user-created tags)
 *
 * Desktop layout:
 *   ┌────────────────────────┬────────────────┐
 *   │  Available Tags (3/5)  │  Your Tags (2/5│
 *   │  [search]  [+ add]     │  • #tag  ×     │
 *   │  #chip #chip #chip … │  • #tag  ×     │
 *   └────────────────────────┴────────────────┘
 *
 * Mobile: single-column stack.
 */
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import {
  FiArrowLeft,
  FiTag,
  FiSearch,
  FiPlus,
  FiX,
  FiEdit2,
  FiTrash2,
  FiCheck,
  FiSave,
} from 'react-icons/fi';
import { Header } from '../../layout/Header';
import { BottomNav } from '../../layout/BottomNav';
import { useAuth } from '../../../hooks/useAuth';
import {
  useGetAllTags,
  useCreateTag,
  useUpdateTag,
  useDeleteTag,
  useUpdateUserTags,
  type Tag,
} from '../../../api/settings';
import { useGetUserProfile } from '../../../api/profile';
import { formatTagLabel } from '../../../utils/tagLabel';

import { extractApiError } from '../../../utils/apiError';
// ── Shared tiny primitives ────────────────────────────────────────────────────

const Spinner = ({ size = 16 }: { size?: number }) => (
  <span
    style={{ width: size, height: size }}
    className="inline-block border-2 border-white/20 border-t-white rounded-full animate-spin"
  />
);

function Modal({ onClose, children }: { onClose: () => void; children: React.ReactNode }) {
  return (
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[100] p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-zinc-900 border border-white/10 rounded-2xl p-6 w-full max-w-sm shadow-2xl">
        {children}
      </div>
    </div>
  );
}

function normalizeTagName(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .replace(/[_-]{2,}/g, (m) => m[0]);
}

function validateTagName(name: string): string | null {
  if (!name.trim()) return 'Name cannot be empty.';
  return null;
}

// ── Main component ────────────────────────────────────────────────────────────

export const ModifyTagsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const { data: allTags = [], isLoading: tagsLoading } = useGetAllTags();
  const {
    data: profile,
    isLoading: profileLoading,
    refetch: refetchProfile,
  } = useGetUserProfile(user?.id);

  const createTagMut = useCreateTag();
  const updateTagMut = useUpdateTag();
  const deleteTagMut = useDeleteTag();
  const saveTagsMut = useUpdateUserTags();

  // ── State ──────────────────────────────────────────────────────────────────
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [initialIds, setInitialIds] = useState<Set<number>>(new Set());
  const [search, setSearch] = useState('');
  const [newTagError, setNewTagError] = useState<string | null>(null);
  const [editDialog, setEditDialog] = useState<Tag | null>(null);
  const [editName, setEditName] = useState('');
  const [editError, setEditError] = useState<string | null>(null);
  const [deleteTagId, setDeleteTagId] = useState<number | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [pendingAutoSelectName, setPendingAutoSelectName] = useState<string | null>(null);
  const [isHydrated, setIsHydrated] = useState(false);
  const prevUserIdRef = useRef<string | number | null>(null);

  // Hydrate from user profile tags
  useEffect(() => {
    if (!profile || !allTags.length) return;
    if (isHydrated) return;
    const userTagNames: string[] = profile.tags ?? [];
    const ids = new Set<number>();
    userTagNames.forEach((name: string) => {
      const found = allTags.find((t) => t.name.toLowerCase() === name.toLowerCase());
      if (found) ids.add(found.id);
    });
    setSelectedIds(new Set(ids));
    setInitialIds(new Set(ids));
    setIsHydrated(true);
  }, [profile, allTags, isHydrated]);

  useEffect(() => {
    const userId = user?.id ?? null;
    if (prevUserIdRef.current !== userId) {
      prevUserIdRef.current = userId;
      setIsHydrated(false);
    }
  }, [user?.id]);

  useEffect(() => {
    if (!pendingAutoSelectName) return;
    const normalizedPending = normalizeTagName(pendingAutoSelectName);
    const created = allTags.find((tag) => normalizeTagName(tag.name) === normalizedPending);
    if (!created) return;
    setSelectedIds((prev) => new Set([...prev, created.id]));
    setPendingAutoSelectName(null);
  }, [allTags, pendingAutoSelectName]);

  const isLoading = tagsLoading || profileLoading;

  const hasChanges = useMemo(() => {
    if (selectedIds.size !== initialIds.size) return true;
    for (const id of selectedIds) if (!initialIds.has(id)) return true;
    return false;
  }, [selectedIds, initialIds]);

  useEffect(() => {
    if (!hasChanges) return undefined;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [hasChanges]);

  const filteredTags = useMemo(
    () => allTags.filter((t) => t.name.toLowerCase().includes(search.toLowerCase())),
    [allTags, search],
  );

  const selectedTagsList = useMemo(
    () => allTags.filter((t) => selectedIds.has(t.id)),
    [allTags, selectedIds],
  );

  // ── Handlers ───────────────────────────────────────────────────────────────
  const toggleTag = useCallback((id: number) => {
    setSelectedIds((prev) => {
      const s = new Set(prev);
      if (s.has(id)) {
        s.delete(id);
      } else {
        s.add(id);
      }
      return s;
    });
  }, []);

  const handleAddTag = async () => {
    if (createTagMut.isPending) return;
    const name = search.trim();
    const err = validateTagName(name);
    if (err) {
      setNewTagError(err);
      return;
    }
    const normalizedName = normalizeTagName(name);
    const duplicateExists = allTags.some((tag) => normalizeTagName(tag.name) === normalizedName);
    if (duplicateExists) {
      setNewTagError('This tag already exists.');
      return;
    }
    setNewTagError(null);
    setPendingAutoSelectName(name);
    try {
      const result = await createTagMut.mutateAsync(name);
      setSearch('');
      if (result?.id) {
        setSelectedIds((prev) => new Set([...prev, result.id]));
        setPendingAutoSelectName(null);
      }
    } catch (error) {
      setPendingAutoSelectName(null);
      setNewTagError(extractApiError(error, 'Could not create tag. Try again.'));
    }
  };

  const handleSave = async () => {
    if (isSaving) return;
    if (!hasChanges) return;
    setIsSaving(true);
    try {
      await saveTagsMut.mutateAsync(Array.from(selectedIds));
      const refreshedProfile = await refetchProfile();
      const refreshedTagNames = Array.isArray(refreshedProfile.data?.tags)
        ? refreshedProfile.data.tags
        : [];
      const refreshedIds = new Set<number>();
      refreshedTagNames.forEach((name: string) => {
        const found = allTags.find((t) => t.name.toLowerCase() === name.toLowerCase());
        if (found) refreshedIds.add(found.id);
      });

      const nextSelectedIds =
        refreshedIds.size > 0 || refreshedTagNames.length === 0
          ? refreshedIds
          : new Set(selectedIds);

      setSelectedIds(new Set(nextSelectedIds));
      setInitialIds(new Set(nextSelectedIds));
      updateUser({
        tags: allTags.filter((tag) => nextSelectedIds.has(tag.id)).map((tag) => tag.name),
      });
      navigate({ to: '/profile/settings' });
    } finally {
      setIsSaving(false);
    }
  };

  const openEdit = (tag: Tag) => {
    setEditDialog(tag);
    setEditName(tag.name);
    setEditError(null);
  };

  const handleEditSave = async () => {
    if (!editDialog) return;
    if (updateTagMut.isPending) return;
    const name = editName.trim();
    const err = validateTagName(name);
    if (err) {
      setEditError(err);
      return;
    }
    const normalizedName = normalizeTagName(name);
    const duplicateExists = allTags.some(
      (tag) => tag.id !== editDialog.id && normalizeTagName(tag.name) === normalizedName,
    );
    if (duplicateExists) {
      setEditError('A tag with this name already exists.');
      return;
    }
    try {
      await updateTagMut.mutateAsync({ id: editDialog.id, name });
      setEditDialog(null);
    } catch (error) {
      setEditError(extractApiError(error, 'Could not update tag. Try again.'));
    }
  };

  const handleDelete = async () => {
    if (deleteTagId === null) return;
    if (deleteTagMut.isPending) return;
    await deleteTagMut.mutateAsync(deleteTagId);
    setSelectedIds((prev) => {
      const s = new Set(prev);
      s.delete(deleteTagId);
      return s;
    });
    setDeleteTagId(null);
  };

  /** User can only edit/delete tags they created (non-global) */
  const canManage = (tag: Tag) =>
    !tag.is_global && tag.creator !== null && String(tag.creator) === String(user?.id);

  /** User-created (non-global) tags owned by the current user */
  const myTags = useMemo(
    () =>
      allTags.filter(
        (t) => !t.is_global && t.creator !== null && String(t.creator) === String(user?.id),
      ),
    [allTags, user?.id],
  );

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-black text-white pb-32">
      <Header />

      <main className="pt-24 md:pt-20 max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 mb-8 text-zinc-500 text-[12px] font-medium">
          <Link
            to="/profile/settings"
            className="flex items-center gap-1.5 hover:text-white transition-colors"
          >
            <FiArrowLeft size={13} className="text-blue-500/80" />
            <span>Settings</span>
          </Link>
          <span className="text-zinc-700">/</span>
          <span className="text-zinc-200">Modify Tags</span>
        </nav>

        {/* Page header */}
        <div className="flex items-start sm:items-center justify-between gap-4 mb-8 flex-col sm:flex-row">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-yellow-500/10 border border-yellow-500/20 flex items-center justify-center shrink-0">
              <FiTag size={20} className="text-yellow-400" />
            </div>
            <div>
              <h1 className="text-white font-bold text-[22px] leading-tight">Modify Tags</h1>
              <p className="text-zinc-500 text-[13px]">
                Add or remove tags that describe your style
              </p>
            </div>
          </div>
          <button
            onClick={handleSave}
            disabled={!hasChanges || isSaving}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-500 disabled:opacity-40 disabled:cursor-not-allowed text-white text-[13px] font-semibold transition-all shrink-0"
          >
            {isSaving ? <Spinner size={14} /> : <FiSave size={14} />}
            Save Changes
          </button>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center h-60">
            <Spinner size={36} />
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            {/* ── Left panel: tag browser ── */}
            <div className="lg:col-span-3 space-y-4">
              <div className="bg-zinc-900/30 border border-white/5 rounded-2xl p-5">
                <h2 className="text-white font-semibold text-[14px] mb-4 flex items-center gap-2">
                  <span className="w-5 h-5 rounded-md bg-yellow-500/20 flex items-center justify-center">
                    <FiTag size={10} className="text-yellow-400" />
                  </span>
                  All Tags
                  <span className="ml-auto text-zinc-600 text-[11px] font-normal font-mono">
                    {allTags.length} total
                  </span>
                </h2>

                {/* Search — shows "Add" button when nothing matches */}
                <div className="relative mb-3">
                  <FiSearch
                    size={13}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none"
                  />
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => {
                      setSearch(e.target.value);
                      setNewTagError(null);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && filteredTags.length === 0 && search.trim()) {
                        handleAddTag();
                      }
                    }}
                    placeholder="Search or create a tag…"
                    className="w-full bg-white/5 border border-white/8 rounded-xl pl-9 pr-8 py-2.5 text-[13px] text-white placeholder:text-zinc-600 focus:outline-none focus:border-blue-500/40 transition-colors"
                  />
                  {search && (
                    <button
                      onClick={() => {
                        setSearch('');
                        setNewTagError(null);
                      }}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300 transition-colors"
                    >
                      <FiX size={12} />
                    </button>
                  )}
                </div>
                {newTagError && <p className="text-red-400 text-[11px] mb-2 ml-1">{newTagError}</p>}

                {/* Tags grid */}
                <div
                  className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-2 overflow-y-auto pr-1 mt-4"
                  style={{ maxHeight: 360 }}
                >
                  {filteredTags.length === 0 ? (
                    <div className="col-span-full flex flex-col items-center gap-3 py-8">
                      <p className="text-zinc-600 text-[13px] text-center">
                        {search ? `No tags matching "${search}"` : 'No tags available yet.'}
                      </p>
                      {search.trim() && (
                        <button
                          onClick={handleAddTag}
                          disabled={createTagMut.isPending}
                          className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-yellow-500/20 border border-yellow-500/30 hover:bg-yellow-500/30 disabled:opacity-40 disabled:cursor-not-allowed text-yellow-300 text-[12px] font-semibold transition-all"
                        >
                          {createTagMut.isPending ? <Spinner size={12} /> : <FiPlus size={12} />}
                          Add &ldquo;{search.trim()}&rdquo;
                        </button>
                      )}
                    </div>
                  ) : (
                    filteredTags.map((tag) => {
                      const isSelected = selectedIds.has(tag.id);
                      const manageable = canManage(tag);
                      return (
                        <div
                          key={tag.id}
                          className={`group flex items-center gap-2 rounded-xl border px-2 py-1.5 transition-all ${
                            isSelected
                              ? 'bg-yellow-500/15 border-yellow-500/40'
                              : 'bg-white/4 border-white/10 hover:border-white/20'
                          }`}
                        >
                          <button
                            onClick={() => toggleTag(tag.id)}
                            className="flex-1 min-w-0 inline-flex items-center gap-1.5 rounded-lg text-[12px] font-medium text-left transition-colors text-zinc-200 hover:text-white"
                          >
                            {isSelected && (
                              <FiCheck size={10} className="text-yellow-400 shrink-0" />
                            )}
                            <span className="truncate">#{formatTagLabel(tag.name)}</span>
                          </button>
                          {manageable && (
                            <div className="flex items-center gap-0.5 shrink-0 opacity-70 group-hover:opacity-100">
                              <button
                                onClick={() => openEdit(tag)}
                                className="p-1 rounded-lg hover:bg-blue-500/15 text-zinc-600 hover:text-blue-400 focus-visible:text-blue-400 transition-colors"
                                title="Edit tag"
                              >
                                <FiEdit2 size={10} />
                              </button>
                              <button
                                onClick={() => setDeleteTagId(tag.id)}
                                className="p-1 rounded-lg hover:bg-red-500/15 text-zinc-600 hover:text-red-400 focus-visible:text-red-400 transition-colors"
                                title="Delete tag"
                              >
                                <FiTrash2 size={10} />
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>

              {/* My custom tags panel (manage edit/delete) */}
              {myTags.length > 0 && (
                <div className="bg-zinc-900/30 border border-white/5 rounded-2xl p-5">
                  <h2 className="text-white font-semibold text-[14px] mb-4 flex items-center gap-2">
                    <span className="w-5 h-5 rounded-md bg-violet-500/20 flex items-center justify-center">
                      <FiEdit2 size={10} className="text-violet-400" />
                    </span>
                    My Custom Tags
                    <span className="ml-auto text-zinc-600 text-[11px] font-normal font-mono">
                      {myTags.length}
                    </span>
                  </h2>
                  <div className="space-y-2">
                    {myTags.map((tag) => (
                      <div
                        key={tag.id}
                        className="flex items-center gap-3 px-4 py-3 bg-white/3 border border-white/[0.07] rounded-xl"
                      >
                        <span className="flex-1 text-white text-[13px]">
                          #{formatTagLabel(tag.name)}
                        </span>
                        <button
                          onClick={() => openEdit(tag)}
                          className="p-1.5 rounded-lg hover:bg-blue-500/15 text-zinc-500 hover:text-blue-400 transition-colors"
                          title="Edit"
                        >
                          <FiEdit2 size={13} />
                        </button>
                        <button
                          onClick={() => setDeleteTagId(tag.id)}
                          className="p-1.5 rounded-lg hover:bg-red-500/15 text-zinc-500 hover:text-red-400 transition-colors"
                          title="Delete"
                        >
                          <FiTrash2 size={13} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* ── Right panel: selected tags ── */}
            <div className="lg:col-span-2">
              <div className="bg-zinc-900/30 border border-white/5 rounded-2xl p-5 lg:sticky lg:top-24">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-white font-semibold text-[14px] flex items-center gap-2">
                    <span className="w-5 h-5 rounded-md bg-blue-500/20 flex items-center justify-center">
                      <FiCheck size={10} className="text-blue-400" />
                    </span>
                    Your Tags
                  </h2>
                  <span className="text-zinc-500 text-[11px] font-mono">
                    {selectedTagsList.length} selected
                  </span>
                </div>

                {selectedTagsList.length === 0 ? (
                  <div className="text-center py-10">
                    <FiTag size={28} className="mx-auto mb-3 text-zinc-700" />
                    <p className="text-zinc-600 text-[13px]">No tags selected yet.</p>
                    <p className="text-zinc-700 text-[11px] mt-1">
                      Click any tag on the left to add it.
                    </p>
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-2 mb-4">
                    {selectedTagsList.map((tag) => (
                      <span
                        key={tag.id}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[12px] bg-yellow-500/15 border border-yellow-500/25 text-yellow-300"
                      >
                        #{formatTagLabel(tag.name)}
                        <button
                          onClick={() => toggleTag(tag.id)}
                          className="hover:text-red-400 transition-colors ml-0.5"
                          title="Remove"
                        >
                          <FiX size={11} />
                        </button>
                      </span>
                    ))}
                  </div>
                )}

                {hasChanges && (
                  <div
                    className={selectedTagsList.length > 0 ? 'border-t border-white/5 pt-4' : ''}
                  >
                    <p className="text-zinc-500 text-[11px] text-center">
                      You have unsaved tag changes.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </main>

      <BottomNav />

      {/* ── Edit Tag Modal ── */}
      {editDialog && (
        <Modal onClose={() => setEditDialog(null)}>
          <h3 className="text-white font-semibold text-[17px] mb-1">Edit Tag</h3>
          <p className="text-zinc-500 text-[12px] mb-4">Rename your custom tag</p>
          <input
            type="text"
            value={editName}
            onChange={(e) => {
              setEditName(e.target.value);
              setEditError(null);
            }}
            onKeyDown={(e) => e.key === 'Enter' && handleEditSave()}
            className={`w-full bg-white/5 border rounded-xl px-4 py-3 text-[14px] text-white focus:outline-none mb-1 transition-colors ${
              editError ? 'border-red-500/50' : 'border-white/10 focus:border-blue-500/40'
            }`}
            autoFocus
          />
          {editError && <p className="text-red-400 text-[11px] mb-3 ml-0.5">{editError}</p>}
          <div className="flex gap-3">
            <button
              onClick={() => setEditDialog(null)}
              className="flex-1 py-2.5 rounded-xl border border-white/10 text-zinc-400 text-[13px] hover:border-white/20 transition-all"
            >
              Cancel
            </button>
            <button
              onClick={handleEditSave}
              disabled={!editName.trim() || updateTagMut.isPending}
              className="flex-1 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-500 disabled:opacity-40 text-white text-[13px] font-semibold transition-all"
            >
              {updateTagMut.isPending ? 'Saving…' : 'Save'}
            </button>
          </div>
        </Modal>
      )}

      {/* ── Delete Tag Modal ── */}
      {deleteTagId !== null && (
        <Modal onClose={() => setDeleteTagId(null)}>
          <h3 className="text-white font-semibold text-[17px] mb-1">Delete Tag</h3>
          <p className="text-zinc-400 text-[13px] mb-6">
            Are you sure you want to delete{' '}
            <strong className="text-white">
              #{formatTagLabel(allTags.find((t) => t.id === deleteTagId)?.name ?? '')}
            </strong>
            ? This action cannot be undone.
          </p>
          <div className="flex gap-3">
            <button
              onClick={() => setDeleteTagId(null)}
              className="flex-1 py-2.5 rounded-xl border border-white/10 text-zinc-400 text-[13px] hover:border-white/20 transition-all"
            >
              Cancel
            </button>
            <button
              onClick={handleDelete}
              disabled={deleteTagMut.isPending}
              className="flex-1 py-2.5 rounded-xl bg-red-600/90 hover:bg-red-600 disabled:opacity-40 text-white text-[13px] font-semibold transition-all"
            >
              {deleteTagMut.isPending ? 'Deleting…' : 'Delete'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
};
